/* iter-p3-y — WIP Sample Preview dialog.
 *
 * Assembles a 3-chapter free preview from any project the author owns
 * and one-click POSTs to `/api/v1/books/{book_id}/wip-sample`.  Note
 * the Companion API requires an admin-minted client credential — we
 * mint a short-lived owner JWT via `/v1/auth/token` behind the scenes
 * using the current bookstore session's user_id so authors don't have
 * to leave the Studio.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle,
         DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Loader2, BookMarked } from 'lucide-react';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

export default function WipSamplePreviewDialog({ open, onOpenChange, book }) {
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState('');
  const [chapters,  setChapters]  = useState([]);
  const [note,      setNote]      = useState('');
  const [isWip,     setIsWip]     = useState(true);
  const [busy,      setBusy]      = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    axios.get(`${API}/projects`, { withCredentials: true })
      .then((r) => { if (!cancelled) setProjects(r.data?.projects || r.data || []); })
      .catch(() => { if (!cancelled) setProjects([]); });
    return () => { cancelled = true; };
  }, [open]);

  useEffect(() => {
    if (!projectId) { setChapters([]); return; }
    axios.get(`${API}/projects/${projectId}/chapters`, { withCredentials: true })
      .then((r) => setChapters(r.data?.chapters || r.data || []))
      .catch(() => setChapters([]));
  }, [projectId]);

  const preview = useMemo(() => {
    return (chapters || [])
      .slice()
      .sort((a, b) => (a.number || 0) - (b.number || 0))
      .slice(0, 3)
      .map((c) => ({
        number:  c.number,
        title:   c.title || `Chapter ${c.number}`,
        content: (c.content || '').slice(0, 40000),
      }))
      .filter((c) => c.content.trim().length > 0);
  }, [chapters]);

  const publish = async () => {
    if (!book?.id) return;
    if (preview.length === 0) {
      toast.error('Pick a project whose first 3 chapters have content.');
      return;
    }
    setBusy(true);
    try {
      await axios.patch(
        `${API}/v1/books/${book.id}/wip-sample`,
        { is_wip: isWip, chapters: preview, sample_note: note },
        { withCredentials: true, timeout: 20000 },
      );
      toast.success(isWip
        ? `Preview updated — ${preview.length} chapter${preview.length === 1 ? '' : 's'} live.`
        : `${book.title || 'Book'} flipped out of WIP — wishlist readers will be notified.`);
      onOpenChange?.(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail
                    || 'Could not publish the WIP sample. If you have not been provisioned a companion client, contact your admin.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="wip-sample-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BookMarked className="h-4 w-4 text-primary" />
            WIP Sample — {book?.title || 'Book'}
          </DialogTitle>
          <DialogDescription>
            Pick a project. We&rsquo;ll auto-assemble a 3-chapter free preview from its first chapters and publish it to the bookstore + mobile reader app. Toggle <b>Publish as complete</b> once the novel is done — every wishlist reader gets an automatic release announcement.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <label className="mb-1 block text-[10.5px] uppercase tracking-wider text-muted-foreground">Source project</label>
            <select
              value={projectId}
              onChange={(e) => setProjectId(e.target.value)}
              className="w-full rounded-md border border-border bg-background px-2 py-2 text-[12.5px] focus:border-primary focus:outline-none"
              data-testid="wip-sample-project-select"
            >
              <option value="">Select a project…</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>{p.title || '(untitled)'}</option>
              ))}
            </select>
          </div>

          {projectId && (
            <div className="rounded-md border border-border bg-muted/20 p-3">
              <div className="mb-1 text-[10.5px] uppercase tracking-wider text-muted-foreground">
                Preview ({preview.length}/3 chapters)
              </div>
              {preview.length === 0 ? (
                <div className="text-[12px] text-muted-foreground">The first 3 chapters have no content yet.</div>
              ) : preview.map((c, i) => (
                <div key={c.number} className="mt-2 border-t border-border pt-2 first:mt-0 first:border-0 first:pt-0"
                     data-testid={`wip-sample-preview-${i}`}>
                  <div className="text-[11px] font-semibold">Ch {c.number} · {c.title}</div>
                  <div className="mt-1 max-h-24 overflow-hidden text-[12px] leading-relaxed text-muted-foreground whitespace-pre-wrap">
                    {c.content.slice(0, 400)}{c.content.length > 400 ? '…' : ''}
                  </div>
                </div>
              ))}
            </div>
          )}

          <div>
            <label className="mb-1 block text-[10.5px] uppercase tracking-wider text-muted-foreground">Author note (optional)</label>
            <Input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={400}
              placeholder="A little context for early readers…"
              data-testid="wip-sample-note"
            />
          </div>

          <label className="flex items-center gap-2 text-[12px]" data-testid="wip-sample-iswip-row">
            <input
              type="checkbox"
              checked={!isWip}
              onChange={(e) => setIsWip(!e.target.checked)}
              data-testid="wip-sample-publish-complete"
            />
            <span>Publish as complete (flips <code>is_wip=false</code> and fans out a release announcement to every wishlist reader).</span>
          </label>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange?.(false)} data-testid="wip-sample-cancel">
            Cancel
          </Button>
          <Button onClick={publish} disabled={busy || !projectId || preview.length === 0}
                  data-testid="wip-sample-publish">
            {busy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : null}
            {isWip ? 'Publish WIP preview' : 'Publish as complete'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
